import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { SwipeEventData, useSwipeable } from 'react-swipeable';
import { useSliderState } from 'src/shared/hooks/useSliderState';
import styled from 'styled-components';

const CarouselOuter = styled.div`
  width: 100%;
  overflow-x: hidden;
  position: relative;
`;

const CarouselInner = styled.div<{ slide: number; totalSlides: number; offset: number }>`
  transition: ${({ offset }) => (offset ? 'all linear' : 'all 0.5s ease')};
  width: calc(100% * ${({ totalSlides }) => totalSlides});
  margin-left: calc(calc(-100% * ${({ slide }) => slide}) + ${({ offset }) => offset}px);
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
`;

interface ISlideProps {
  totalSlides?: number;
}
export const CarouselSlide = styled.div<ISlideProps>`
  width: calc(100% / ${({ totalSlides }) => totalSlides});
`;

const isCarouselSlide = (x: any): x is React.ReactElement<ISlideProps> =>
  React.isValidElement(x) && x.type === CarouselSlide;

export interface IOnChangeArguments {
  index: number;
  action: 'click' | 'swipe' | 'auto';
  direction: 'right' | 'left';
}

interface ICarouselProps {
  className?: string;
  leftArrow?: JSX.Element;
  rightArrow?: JSX.Element;
  indicator?: JSX.Element;
  clickableIndicatorComponent?: React.FC<{ goto: (index: number) => void }>;
  onChange?: (args: IOnChangeArguments) => void;
  playOnVisible?: boolean;
  autoplay?: boolean;
  autoplayInterval?: number;
  renderAll?: boolean;
}
export const Carousel: React.FC<React.PropsWithChildren<ICarouselProps>> = ({
  children,
  className,
  leftArrow,
  rightArrow,
  indicator,
  clickableIndicatorComponent: ClickableIndicatorComponent,
  onChange,
  playOnVisible,
  autoplay,
  autoplayInterval = 3000,
  renderAll,
}) => {
  const childrenArray = React.Children.toArray(children);
  const slides = childrenArray.filter(isCarouselSlide);
  const [playEnabled, setPlayEnabled] = React.useState<boolean>(autoplay ?? false);
  const lastClickedTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [isClickingThroughSlides, setIsClickingThroughSlides] = React.useState(false);

  const stopAutoPlay = useCallback(() => {
    setPlayEnabled(false);
  }, []);

  useEffect(() => {
    if (slides.length !== childrenArray.length) {
      console.error('[Carousel] All children must be CarouselSlides');
    }
  }, [childrenArray.length, slides.length]);

  const { ref: isVisibleRef } = useInView({
    triggerOnce: true,
    onChange: (inView) => {
      if (playOnVisible && inView) {
        setPlayEnabled(true);
      }
    },
  });

  const { increment, decrement, slide, isFirst, isLast, goto } = useSliderState(slides.length);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    let id: ReturnType<typeof setInterval>;
    if (playEnabled) {
      id = setInterval(() => {
        let newSlide = 0;
        if (isLast) {
          goto(0);
        } else {
          newSlide = increment();
        }
        onChange &&
          onChange({
            index: newSlide,
            action: 'auto',
            direction: 'right',
          });
      }, autoplayInterval);
    }
    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [goto, increment, isLast, onChange, playEnabled]);

  const withSwipe = useCallback(
    (cb: () => number) => () => {
      const newSlide = cb();
      setOffset(0);
      onChange &&
        onChange({
          index: newSlide,
          action: 'swipe',
          direction: newSlide - slide > 0 ? 'right' : 'left',
        });
    },
    [onChange, slide]
  );
  const withClick = useCallback(
    (cb: () => number) => (evt: React.MouseEvent) => {
      evt.preventDefault();
      evt.stopPropagation();
      stopAutoPlay();
      const newSlide = cb();

      onChange &&
        onChange({
          index: newSlide,
          action: 'swipe',
          direction: newSlide - slide > 0 ? 'right' : 'left',
        });

      setIsClickingThroughSlides(true);
      if (lastClickedTimeoutRef.current) {
        clearTimeout(lastClickedTimeoutRef.current);
      }
      lastClickedTimeoutRef.current = setTimeout(() => {
        setIsClickingThroughSlides(false);
      }, 750);
    },
    [onChange, slide, stopAutoPlay]
  );

  const onSwipedLeft = useMemo(() => withSwipe(increment), [withSwipe, increment]);
  const onSwipedRight = useMemo(() => withSwipe(decrement), [withSwipe, decrement]);
  const onSwiping = useCallback(
    (data: SwipeEventData) => {
      if (data.dir === 'Left' || data.dir === 'Right') {
        data.event.preventDefault();
        setOffset(data.deltaX);
        stopAutoPlay();
      }
    },
    [stopAutoPlay]
  );
  const onSwiped = useCallback(() => setOffset(0), []);
  const { ref } = useSwipeable({
    trackTouch: true,
    onSwipedLeft,
    onSwipedRight,
    onSwiping,
    onSwiped,
  });

  const renderableSlides = useMemo(
    () =>
      slides
        .slice(0, renderAll ? slides.length : Math.min(slide + 2, slides.length))
        .map((s) => React.cloneElement(s, { totalSlides: slides.length })),
    [renderAll, slide, slides]
  );

  const [boundLeft, boundRight] = useMemo(() => {
    const left = leftArrow ? React.cloneElement(leftArrow, { onClick: withClick(decrement) }) : undefined;
    const right = rightArrow ? React.cloneElement(rightArrow, { onClick: withClick(increment) }) : undefined;
    return [left, right];
  }, [decrement, increment, leftArrow, rightArrow, withClick]);

  const handleOnClick = useCallback(
    (event: React.MouseEvent) => {
      // When a user is still clicking through slides, we prevent the click event from bubbling in order to
      // prevent accidental misclicks when they reach the end of the slides.
      if (isClickingThroughSlides) {
        event.preventDefault();
      }
    },
    [isClickingThroughSlides]
  );

  return (
    <CarouselOuter ref={isVisibleRef} className={className} onClick={handleOnClick}>
      <CarouselInner ref={ref} slide={slide} totalSlides={slides.length} offset={offset}>
        {renderableSlides}
      </CarouselInner>
      {!isFirst && boundLeft}
      {!isLast && boundRight}
      {indicator}
      {ClickableIndicatorComponent && React.createElement(ClickableIndicatorComponent, { goto })}
    </CarouselOuter>
  );
};
